// Super-Admin — Edit lesson (title, description, duration, min-watch %, order).
// For video lessons also exposes a "Replace video" action which opens the
// existing AddVideoDialog (a new upload replaces the current video record).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { AddVideoDialog } from "./AddVideoDialog";

export function EditLessonDialog({ open, onOpenChange, lesson, onSuccess }) {
  const [form, setForm] = useState({
    lesson_title: "",
    lesson_description: "",
    estimated_duration_minutes: 10,
    minimum_watch_percentage: 100,
    display_order: 0,
  });
  const [saving, setSaving] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);

  useEffect(() => {
    if (open && lesson) {
      setForm({
        lesson_title: lesson.lesson_title || "",
        lesson_description: lesson.lesson_description || "",
        estimated_duration_minutes: lesson.estimated_duration_minutes ?? 10,
        minimum_watch_percentage: lesson.minimum_watch_percentage ?? 100,
        display_order: lesson.display_order ?? 0,
      });
    }
  }, [open, lesson]);

  if (!lesson) return null;

  const isVideo = lesson.lesson_type === "video";

  const submit = async () => {
    if (!form.lesson_title.trim()) {
      toast.error("Title is required.");
      return;
    }
    const mw = Number(form.minimum_watch_percentage);
    if (Number.isNaN(mw) || mw < 1 || mw > 100) {
      toast.error("Min watch % must be between 1 and 100.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/lessons/${lesson.id}`, {
        lesson_title: form.lesson_title.trim(),
        lesson_description: form.lesson_description || null,
        estimated_duration_minutes: parseInt(form.estimated_duration_minutes || 0, 10),
        minimum_watch_percentage: parseInt(mw, 10),
        display_order: parseInt(form.display_order || 0, 10),
      });
      toast.success("Lesson updated.");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-lesson-dialog">
          <DialogHeader>
            <DialogTitle>Edit lesson</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input
                value={form.lesson_title}
                onChange={(e) => setForm({ ...form, lesson_title: e.target.value })}
                data-testid="edit-lesson-title"
              />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={form.lesson_description}
                onChange={(e) => setForm({ ...form, lesson_description: e.target.value })}
                data-testid="edit-lesson-description"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  value={form.estimated_duration_minutes}
                  onChange={(e) => setForm({ ...form, estimated_duration_minutes: e.target.value })}
                  data-testid="edit-lesson-duration"
                />
              </div>
              <div className="space-y-1">
                <Label>Min watch %</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={form.minimum_watch_percentage}
                  onChange={(e) => setForm({ ...form, minimum_watch_percentage: e.target.value })}
                  data-testid="edit-lesson-min-watch"
                />
              </div>
              <div className="space-y-1">
                <Label>Order</Label>
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  data-testid="edit-lesson-order"
                />
              </div>
            </div>

            {isVideo && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold text-slate-700">Video</div>
                    <div className="text-[11px] text-slate-500">
                      Uploading a new video will replace the current one for this lesson.
                    </div>
                  </div>
                  <button
                    onClick={() => setReplaceOpen(true)}
                    className="inline-flex items-center gap-1 rounded-md border border-[#101876]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#101876] hover:bg-[#101876]/5"
                    data-testid="edit-lesson-replace-video"
                  >
                    <RotateCw className="h-3 w-3" /> Replace video
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-60"
              data-testid="edit-lesson-save"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace video — reuses the same upload dialog. After a successful
          upload we soft-delete the older lesson_video record(s) so learners
          only ever see the newest one. */}
      {isVideo && (
        <AddVideoDialog
          open={replaceOpen}
          onOpenChange={setReplaceOpen}
          lessonId={lesson.id}
          onSuccess={async () => {
            try {
              await api.post(`/admin/lessons/${lesson.id}/videos/cleanup-old`);
            } catch (err) {
              // Cleanup is best-effort — the newest video is picked by the
              // learner endpoint automatically anyway.
              console.warn("Cleanup old videos failed:", formatApiError(err));
            }
            toast.success("Video replaced.");
            onSuccess?.();
            setReplaceOpen(false);
          }}
        />
      )}
    </>
  );
}
